import { useEffect, useState } from "react";
import { Sparkles, X, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";

const MYLOCALGO_URL =
  process.env.REACT_APP_MYLOCALGO_URL ||
  "https://mylocalgo.in?utm_source=balloonbites";

// Banner dismisses for the browser session only (comes back on refresh next day).
const KEY = "bb_promo_dismissed_v1";

export default function PromoBanner() {
  const [offer, setOffer] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(KEY) === "1") setDismissed(true);
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/settings/mylocalgo");
        if (alive) setOffer(data);
      } catch {
        if (alive) setOffer({ is_active: false });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!offer || !offer.is_active || dismissed) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    sessionStorage.setItem(KEY, "1");
    setDismissed(true);
  };

  const offerLabel =
    offer.discount_type === "percent"
      ? `up to ${offer.discount_value}% off`
      : `flat ₹${offer.discount_value} off`;

  return (
    <a
      href={MYLOCALGO_URL}
      target="_blank"
      rel="noreferrer"
      data-testid="promo-banner"
      className="group relative block overflow-hidden border-b border-[#e8c9c9] bg-gradient-to-r from-[#f7dede] via-[#f4cbcb] to-[#f7dede] text-[#5a2b2b] transition-colors hover:from-[#f4cbcb] hover:via-[#efb8b8] hover:to-[#f4cbcb]"
      style={{ minHeight: "76px" }}
    >
      {/* Decorative sparkles */}
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <div className="absolute -left-6 top-3 h-16 w-16 rounded-full bg-white/40 blur-2xl" />
        <div className="absolute right-24 bottom-2 h-14 w-14 rounded-full bg-white/30 blur-xl" />
      </div>

      <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-5 px-5 py-4 lg:px-10">
        <div className="flex flex-1 items-center gap-3 sm:gap-4">
          <div className="hidden shrink-0 rounded-full bg-white/70 p-2.5 shadow-sm ring-1 ring-white/60 sm:block">
            <Sparkles className="h-5 w-5 text-[#d08c8c]" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 font-serif text-base leading-tight sm:text-lg">
              <span className="font-semibold tracking-wide text-[#3a1a1a]">
                MyLocalGo partner offer
              </span>
              <span
                className="hidden rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-[#8a4a4a] ring-1 ring-white/60 sm:inline"
                data-testid="promo-banner-offer"
              >
                {offerLabel}
              </span>
            </div>
            <div className="mt-1 text-xs text-[#6b3d3d] sm:text-sm">
              <span className="hidden sm:inline">
                Grab a redemption code on MyLocalGo &amp; apply at checkout —{" "}
                {offerLabel} on Hampers &amp; Gifts
                {offer.min_order ? ` (orders above ₹${offer.min_order})` : ""}.
                Customised Chocolates are enquiry-only and not eligible.
              </span>
              <span className="sm:hidden">
                Tap for a code · {offerLabel} · Hampers &amp; Gifts only
              </span>
            </div>
          </div>
        </div>

        <span
          data-testid="promo-banner-cta"
          className="hidden shrink-0 items-center gap-1.5 rounded-full bg-[#d08c8c] px-5 py-2 text-sm font-medium text-white shadow-sm transition-transform group-hover:-translate-y-0.5 group-hover:bg-[#b87a7a] sm:inline-flex"
        >
          Redeem <ArrowUpRight className="h-4 w-4" />
        </span>

        <button
          onClick={dismiss}
          data-testid="promo-banner-close"
          className="shrink-0 rounded-full p-1.5 text-[#5a2b2b]/70 transition-colors hover:bg-white/60 hover:text-[#5a2b2b]"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </a>
  );
}
